import {
	faArrowRight,
	faChevronLeft,
	faChevronRight,
	faGrid2,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResilientImage, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontCategoryTiles } from '../../../services/portalDashboardService';

/**
 * Category Tiles widget — horizontal-scrolling row of clickable
 * category cards (image + name) routing to `/catalog?category=<name>`.
 * Designed for the same homepage shape as the item carousels: tile-
 * width is fixed so all 3 row widgets read as a coherent set.
 *
 * Anonymous-safe (categories aren't customer-scoped). Categories
 * without an admin-uploaded image render a name-on-gradient
 * placeholder so the row never has visual holes.
 */
export function CategoryTilesWidget({
	title,
	limit,
	density = 'standard',
}: {
	title?: string;
	limit?: number;
	/**
	 * Density preset. `compact` shrinks per-tile width on mobile (~30%
	 * narrower) so the row fits roughly one extra category per viewport
	 * before swiping. Desktop unchanged. Set per widget instance via
	 * `widgetConfig.density`.
	 */
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const queryKey = ['portal', 'dashboard', 'category-tiles', limit ?? null];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontCategoryTiles(token, limit),
		staleTime: 10 * 60_000,
	});
	const categories = data?.data ?? [];

	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (el == null) return;
		setCanScrollLeft(el.scrollLeft > 4);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateArrows();
		const el = scrollerRef.current;
		if (el == null) return;
		el.addEventListener('scroll', updateArrows, { passive: true });
		window.addEventListener('resize', updateArrows);
		return () => {
			el.removeEventListener('scroll', updateArrows);
			window.removeEventListener('resize', updateArrows);
		};
	}, [updateArrows, categories.length]);

	// Auto-hide when there are no categories with shoppable items for the
	// active brand. A brand-new tenant with no item.category values
	// populated would otherwise show a permanent "No categories" tile —
	// better to omit the row entirely. This early return MUST stay below
	// every hook above (useQuery / useCallback / useEffect): returning
	// before them is a rules-of-hooks violation that throws "rendered
	// fewer hooks than expected" the moment the row re-renders into the
	// empty state after having shown categories — which blanks the whole
	// storefront (and leaves the mobile app stuck on the splash screen).
	if (!isLoading && !isError && categories.length === 0) return null;

	const scrollByViewport = (direction: 1 | -1) => {
		const el = scrollerRef.current;
		if (el == null) return;
		const delta = el.clientWidth * 0.85 * direction;
		el.scrollBy({ left: delta, behavior: 'smooth' });
	};

	return (
		<section className="w-full h-full flex flex-col">
			<header className="flex items-center justify-between mb-3">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faGrid2} className="text-portal-accent" />
					{t(title ?? 'Shop by category')}
				</h2>
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
				>
					{t('Browse all')}
					<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
				</button>
			</header>

			{isLoading ? (
				<EmptyMessage message={t('Loading categories...')} />
			) : isError ? (
				<EmptyMessage message={t("We couldn't load categories right now.")} />
			) : categories.length === 0 ? (
				<EmptyMessage message={t('No categories to show.')} />
			) : (
				<div className="relative flex-1">
					<div
						ref={scrollerRef}
						className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1"
						style={{ scrollbarWidth: 'thin' }}
					>
						{categories
							.filter(
								// Defensive: skip tiles without a usable name. The
								// backend SQL already filters out NULL/empty, but if a
								// blank slips through the click would produce
								// `/catalog?category=` — which the catalog page treats
								// as "no filter", indistinguishable from `/catalog`
								// alone. That's exactly the symptom we're avoiding.
								(cat) => cat.name != null && cat.name.trim() !== '',
							)
							.map((cat) => (
								<button
									key={cat.name}
									type="button"
									onClick={() => {
										// Mobile-Safari quirk: an empty query value
										// can survive `encodeURIComponent`, so we
										// belt-and-braces on click too. Trim before
										// encoding because URL params are
										// whitespace-sensitive but the catalog
										// filter matches on raw `item.category`
										// equality.
										const param = cat.name.trim();
										if (param === '') {
											navigate('/catalog');
											return;
										}
										navigate(`/catalog?category=${encodeURIComponent(param)}`);
									}}
									className={`snap-start shrink-0 ${
										density === 'compact'
											? 'w-[80px] sm:w-[110px] md:w-[160px]'
											: 'w-[112px] sm:w-[140px] md:w-[160px]'
									} text-left bg-brand-card border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow`}
								>
									<div className="aspect-square bg-brand-input flex items-center justify-center relative overflow-hidden">
										<ResilientImage
											src={cat.imageUrl}
											alt={cat.displayName}
											className="w-full h-full object-cover"
											loading="lazy"
											onLikelyStaleUrl={() => {
												void queryClient.invalidateQueries({ queryKey });
											}}
											fallback={
												<div
													className="w-full h-full flex items-center justify-center p-3 text-center"
													style={{
														background:
															'linear-gradient(135deg, #fbbf24, #f59e0b)',
													}}
												>
													<span className="text-white font-bold text-sm md:text-base drop-shadow line-clamp-3">
														{t(cat.displayName)}
													</span>
												</div>
											}
										/>
									</div>
									<div className="p-2">
										<p className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
											{t(cat.displayName)}
										</p>
										<p className="text-[11px] text-brand-muted">
											{cat.itemCount} {t('items')}
										</p>
									</div>
								</button>
							))}
					</div>

					{canScrollLeft && (
						<button
							type="button"
							onClick={() => scrollByViewport(-1)}
							aria-label={t('Scroll left')}
							className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-white shadow-lg border border-brand-border hover:bg-brand-input z-10"
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
					)}
					{canScrollRight && (
						<button
							type="button"
							onClick={() => scrollByViewport(1)}
							aria-label={t('Scroll right')}
							className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-white shadow-lg border border-brand-border hover:bg-brand-input z-10"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					)}
				</div>
			)}
		</section>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}
